type NormalizeCapturedErrorOptions = {
  area: string;
  operation: string;
};

type NormalizedCapturedError = {
  error: Error;
  extra: Record<string, unknown>;
};

const MAX_SERIALIZED_DEPTH = 4;
const MAX_SERIALIZED_KEYS = 30;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const hasUsableStack = (error: Error) =>
  typeof error.stack === "string" && error.stack.trim().length > 0;

const getConstructorName = (value: unknown) => {
  if (!isRecord(value)) {
    return typeof value;
  }

  return value.constructor?.name ?? "Object";
};

const serializeErrorValue = (
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      cause:
        "cause" in value
          ? serializeErrorValue(value.cause, depth + 1, seen)
          : undefined,
    };
  }

  if (!isRecord(value)) {
    return value;
  }

  if (seen.has(value)) {
    return "[Circular]";
  }

  if (depth >= MAX_SERIALIZED_DEPTH) {
    return `[${getConstructorName(value)}]`;
  }

  seen.add(value);

  const entries = Object.entries(value).slice(0, MAX_SERIALIZED_KEYS);
  const serialized = Object.fromEntries(
    entries.map(([key, nestedValue]) => [
      key,
      serializeErrorValue(nestedValue, depth + 1, seen),
    ]),
  );

  if (Object.keys(value).length > MAX_SERIALIZED_KEYS) {
    serialized.__truncatedKeys =
      Object.keys(value).length - MAX_SERIALIZED_KEYS;
  }

  seen.delete(value);
  return serialized;
};

export const getCapturedErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (isRecord(error) && typeof error.message === "string" && error.message) {
    return error.message;
  }

  if (isRecord(error) && typeof error.underlyingErrorMessage === "string") {
    return error.underlyingErrorMessage;
  }

  if (isRecord(error) && typeof error.code === "string") {
    return error.code;
  }

  return String(error);
};

const buildNormalizedError = (
  error: unknown,
  { operation }: NormalizeCapturedErrorOptions,
) => {
  const message = getCapturedErrorMessage(error);
  const normalizedError = new Error(`${operation}: ${message}`);
  normalizedError.name =
    error instanceof Error && error.name ? error.name : "CapturedError";

  try {
    (normalizedError as Error & { cause?: unknown }).cause = error;
  } catch {
    // Some JS runtimes expose Error#cause as read-only; extra still carries it.
  }

  return normalizedError;
};

export const normalizeCapturedError = (
  error: unknown,
  options: NormalizeCapturedErrorOptions,
): NormalizedCapturedError => {
  const serializedOriginalError = serializeErrorValue(error);
  const baseExtra = {
    capturedError: serializedOriginalError,
    capturedErrorType: getConstructorName(error),
    capturedErrorWasNormalized: false,
  };

  if (error instanceof Error && hasUsableStack(error)) {
    return {
      error,
      extra: baseExtra,
    };
  }

  return {
    error: buildNormalizedError(error, options),
    extra: {
      ...baseExtra,
      capturedErrorWasNormalized: true,
    },
  };
};
